import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Polygon, CircleMarker, Popup, LayersControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalLink } from "lucide-react";
import { useLang } from "@/lib/i18n";

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [24, 24] });
  }, [bounds, map]);
  return null;
};

export const ParcelMapPreview = ({ geometry, report }) => {
  const { t } = useLang();

  const model = useMemo(() => {
    const rings = geometry?.rings;
    if (!rings || !rings.length) return null;
    // Convert ArcGIS [lng,lat] rings -> Leaflet [lat,lng]
    const positions = rings.map((ring) => ring.map(([lng, lat]) => [lat, lng]));
    const flat = positions.flat();
    const lats = flat.map((p) => p[0]);
    const lngs = flat.map((p) => p[1]);
    const bounds = [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
    const center = [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
    return { positions, bounds, center };
  }, [geometry]);

  const zoning = report?.zoning;
  const juris = report?.jurisdiction;

  return (
    <div data-testid="parcel-map-container" className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t("map.header")}</h3>
        {model && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${model.center[0]},${model.center[1]}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
          >
            {t("map.openMaps")} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {model ? (
        <>
          <div className="overflow-hidden rounded-lg border border-border" data-testid="leaflet-map" style={{ height: 260 }}>
            <MapContainer key={report?.folio || "map"} center={model.center} zoom={18} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Street">
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellite">
                  <TileLayer
                    attribution="Tiles &copy; Esri"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              <Polygon positions={model.positions} pathOptions={{ color: "#0b3b73", weight: 3, fillColor: "#f59e0b", fillOpacity: 0.18 }}>
                <Popup>
                  <div style={{ fontSize: 12 }}>
                    <strong>{report?.site_address || ""}</strong>
                    <br />
                    {juris ? `${juris}` : ""}
                    {zoning ? ` · ${t("sum.zoning")}: ${zoning}` : ""}
                  </div>
                </Popup>
              </Polygon>
              <CircleMarker center={model.center} radius={5} pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 1 }} />
              <FitBounds bounds={model.bounds} />
            </MapContainer>
          </div>
          <p className="mt-3 font-mono text-[10px] text-muted-foreground">
            {t("map.centroid", { lat: model.center[0].toFixed(6), lng: model.center[1].toFixed(6) })}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t("map.none")}</p>
      )}
    </div>
  );
};
